import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Star } from 'lucide-react';

import { feedback as feedbackApi } from '../../lib/api';
import {
  Card,
  CardBody,
  CardHeader,
  Stack,
} from '../ui-kit';
import { StarRating } from './StarRating';

const SummaryHeader = ({ summary, t }) => {
  const avg = summary?.avg_rating;
  return (
    <div className="flex items-baseline gap-4 flex-wrap">
      <div className="flex items-baseline gap-2">
        <span
          className="font-light text-4xl text-text"
          data-testid="coach-feedback-avg"
        >
          {avg != null ? avg.toFixed(1) : '—'}
        </span>
        <span className="text-text-muted text-sm">/ 5</span>
      </div>
      <StarRating
        value={Math.round(avg || 0)}
        readOnly
        size={20}
        testId="coach-feedback-avg-stars"
      />
      <span className="text-text-muted text-sm">
        {t('feedback.totalReviews')}: {summary?.total_reviews ?? 0}
      </span>
    </div>
  );
};

const DistributionBar = ({ distribution, total }) => {
  const rows = [5, 4, 3, 2, 1];
  return (
    <div className="space-y-1.5" data-testid="coach-feedback-distribution">
      {rows.map((star) => {
        const count = distribution[star] || 0;
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={star} className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1 w-10 text-text-muted">
              {star} <Star size={12} strokeWidth={1.5} fill="#2B7CFF" color="#2B7CFF" />
            </div>
            <div className="flex-1 h-2 bg-background-subtle rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
                data-testid={`coach-feedback-dist-${star}`}
              />
            </div>
            <span className="text-text-muted w-10 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

const AnonymousReviewRow = ({ review, t }) => (
  <div
    className="border-t border-border pt-3 first:border-t-0 first:pt-0"
    data-testid={`coach-feedback-row-${review.id}`}
  >
    <div className="flex items-center justify-between gap-2 mb-1">
      <StarRating value={review.rating} readOnly size={16} />
      <span className="text-xs text-text-muted">
        {new Date(review.created_at).toLocaleDateString()}
      </span>
    </div>
    {review.comment && (
      <p className="text-sm text-text-secondary whitespace-pre-wrap">
        {review.comment}
      </p>
    )}
    {review.reply_count > 0 && (
      <div className="flex items-center gap-1 mt-2 text-xs text-text-muted">
        <MessageCircle size={12} />
        {review.reply_count}
      </div>
    )}
  </div>
);

/**
 * Coach-facing aggregator widget. Anonymous by design:
 *   - Never queries `athlete_id`.
 *   - Hides aggregates until k-anonymity floor (≥3 reviews).
 */
export const CoachFeedbackCard = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        feedbackApi.mySummary(),
        feedbackApi.myFeed({ limit: 10 }),
      ]);
      setSummary(s.data);
      setReviews(r.data);
    } catch (error) {
      console.error('Failed to load coach feedback:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const floor = summary?.floor ?? 3;
  const eligible = summary?.eligible;

  const content = useMemo(() => {
    if (loading) {
      return (
        <p className="text-text-muted text-center py-8">…</p>
      );
    }
    if (!eligible) {
      return (
        <div
          className="text-center py-8"
          data-testid="coach-feedback-not-eligible"
        >
          <p className="text-text-muted">{t('feedback.notEnoughYet')}</p>
          <p className="text-xs text-text-muted mt-2">
            {t('feedback.kAnonymityNote', { floor })}
          </p>
        </div>
      );
    }
    return (
      <Stack gap={6}>
        <SummaryHeader summary={summary} t={t} />
        <DistributionBar
          distribution={summary?.distribution || {}}
          total={summary?.total_reviews || 0}
        />
        {reviews.length === 0 ? (
          <p className="text-text-muted text-sm">{t('feedback.noReviews')}</p>
        ) : (
          <div>
            <p className="text-sm font-medium text-text mb-3">
              {t('feedback.recentReviews')}
            </p>
            <div className="space-y-3">
              {reviews.map((rev) => (
                <AnonymousReviewRow key={rev.id} review={rev} t={t} />
              ))}
            </div>
          </div>
        )}
      </Stack>
    );
  }, [loading, eligible, summary, reviews, floor, t]);

  return (
    <Card data-testid="coach-feedback-card">
      <CardHeader title={t('feedback.title')} />
      <CardBody>{content}</CardBody>
    </Card>
  );
};

export default CoachFeedbackCard;
